// Import Ionic Components
import {
  IonCard,
  IonCol,
  IonLoading,
  IonRow,
  isPlatform,
  useIonToast,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from "@ionic/react";
import { Browser } from "@capacitor/browser";
import { App } from "@capacitor/app";
import { PluginListenerHandle } from "@capacitor/core";

// Import React Libraries
import React, { useMemo, useRef, useState } from "react";
import { useHistory } from "react-router";

import dayjs from "dayjs";

// Import providers
import { AppMetricaProvider } from "../../providers/AppMetrica/AppMetrica";

// Import Css File
import classNames from "classnames/bind";
import styles from "./order.module.scss";

// Import custom components
import ContinueButton from "../../components/ContinueButton/ContinueButton";
import OrderSummaryCoupons from "../../components/OrderSummary/OrderSummaryCoupons";
import OrderSummaryDetails from "../../components/OrderSummary/OrderSummaryDetails";
import OrderSummaryNote from "../../components/OrderSummary/OrderSummaryNote";
import OrderSummaryProductsList from "../../components/OrderSummary/OrderSummaryProductsList";
import OrderSummaryTotal from "../../components/OrderSummary/OrderSummaryTotal";
import OrderSummaryWhatsapp from "../../components/OrderSummary/OrderSummaryWhatsapp";
import MaxWidthContainer from "../../components/UI/MaxWidthContainer/MaxWidthContainer";
import {
  CreateOrderData,
  LineItem,
  PaymentMethodIdEnum,
  Product,
  ProductType,
  ShippingMethodDeliveryType,
} from "@core/types";
import { useAuth } from "../../contexts/auth-context";
import { useCreateOrderMutation, useCreatePaymentMutation } from "../../providers/Orders";
import { useOrderData } from "../../providers/orderDataStore";
import { useConfig } from "../../providers/config";
import { useCartData } from "../../providers/cartDataStore";
import { useGetIncludeProductsQuery } from "../../providers/products";
import { Coupon } from "../../providers/coupons";

import Page from "../page-layout";
import { useMarketMode } from "../../providers/marketMode";
import { useActiveOrMainShippingZone } from "../../providers/shippingZones";
import useGetCurrentAddress from "../../hooks/useGetCurrentAddress";
import { useTranslation } from "react-i18next";
import { useGetCustomerAddresses } from "../../providers/CustomerAddresses";
import { useCustomerAddressesStorageState } from "../../providers/customerAddressesStorage/useCustomerAddressesStorage";

const cx = classNames.bind(styles);

const Order = () => {
  const { t } = useTranslation();
  const { user, isUserAuthenticated } = useAuth();
  let history = useHistory(); // For Navigation
  const { cartProducts, cartSubtotalValue, cartTotalValue, coupons, resetCart, rewardPoints } = useCartData();

  const [showLoading, setShowLoading] = useState(false); // For Loading
  const [showToast] = useIonToast();

  const { mutate: createOrderMutation } = useCreateOrderMutation();
  const config = useConfig();

  const { activeShippingZone } = useActiveOrMainShippingZone();
  const { currentShippingAddressId } = useCustomerAddressesStorageState();

  const { data: products, isLoading: isProductsLoading } = useGetIncludeProductsQuery({
    include: cartProducts.map((product) => product.id),
    params: {
      zoneId: activeShippingZone.id,
    },
    options: {
      enabled: !!cartProducts.length && !!activeShippingZone.id,
    },
  });
  const order = useOrderData();

  const { mutate: createPaymentMutation } = useCreatePaymentMutation();
  const { mode } = useMarketMode();

  const { data: customerAddresses } = useGetCustomerAddresses({
    customerId: user?.id,
    options: { enabled: !!isUserAuthenticated },
  });

  const address = useGetCurrentAddress() || customerAddresses?.find((addr) => addr.id === currentShippingAddressId);

  const submit = async () => {
    setShowLoading(true);

    const requestData: CreateOrderData = {
      deliveryDate:
        order.shippingMethod?.methodDeliveryType === ShippingMethodDeliveryType.default
          ? (order.deliveryDate ?? undefined)
          : undefined,
      deliveryTime:
        order.shippingMethod?.methodDeliveryType === ShippingMethodDeliveryType.default
          ? order.deliveryInterval
          : undefined,

      paymentMethod: order.paymentMethod!,

      address: {
        city: address?.city ?? "",
        house: address?.house ?? "",
        isFlat: address?.isFlat,
        apartmentNumber: address?.apartmentNumber ?? "",
        street: address?.street ?? "",
        entrance: address?.entrance ?? "",
        floor: address?.floor ?? "",
        fullAddress: address?.fullAddress ?? "",
        intercom: address?.intercom ?? "",
        id: address?.id ?? "",
        longitude: address?.longitude ?? "",
        latitude: address?.latitude ?? "",
      },

      customer: {
        id: user?.id ?? "",
        firstName: order.firstName ?? "",
        lastName: order.lastName ?? "",
        phone: order.phoneNumber ?? "",
        whatsappNumber: order.whatsAppNumber ?? "",
        email: user?.email,
      },

      shippingLine: {
        methodId: order.shippingMethod!.methodId,
        methodTitle: order.shippingMethod!.title,
        shipId: order.shippingMethod!.id,
        total: order.shippingMethod!.cost ?? "0",
      },
      lineItems: cartProducts.map((cartItem) => {
        const lineItem: LineItem = {
          id: cartItem.parentId,
          quantity: cartItem.quantity,
          price: cartItem.price,
          name: cartItem.name,
          unitName: cartItem.unitName,
        };

        console.log("📋 [order.tsx] Creating lineItem:", {
          cartItemName: cartItem.name,
          cartItemUnitName: cartItem.unitName,
          lineItemUnitName: lineItem.unitName,
          cartItem: cartItem,
        });

        if (cartItem.type === ProductType.VARIATION) {
          lineItem.variationId = cartItem.id;
        }

        if (coupons.length > 0) {
          let itemTotalPrice = cartItem.quantity * parseFloat(cartItem.price);
          coupons.forEach((coupon) => {
            itemTotalPrice -= Coupon.calculateDiscountForItem(coupon, cartItem, cartSubtotalValue);
          });

          lineItem.total = String(itemTotalPrice);
        }
        return lineItem;
      }),
      customerNote: order.customerNote,
      total: cartTotalValue,
      coupons: coupons,
      rewardPoints: rewardPoints ?? 0,
    };

    if (rewardPoints > 0) {
      requestData.rewardPoints = rewardPoints;
    }

    const finishOrder = (responseData: unknown) => {
      AppMetricaProvider.reportEvent("orderCreate", {
        data_in_request: requestData,
        data_in_response: responseData,
      });
      resetCart(isUserAuthenticated);
      setShowLoading(false);
      history.replace("/thank-you");
    };

    console.log("🚀 [order.tsx] About to create order with requestData:", {
      requestData: requestData,
      lineItems: requestData.lineItems,
      firstLineItem: requestData.lineItems?.[0],
    });

    createOrderMutation(requestData, {
      onSuccess(orderData) {
        console.log("✅ [order.tsx] Order created successfully:", orderData);

        if (
          config?.isOnlinePaymentEnabled &&
          orderData.paymentMethod?.type === PaymentMethodIdEnum.card &&
          orderData.id &&
          orderData.number
        ) {
          const paymentPayload = {
            amount: Math.trunc(requestData.total * 100),
            orderId: String(orderData.id),
            orderNumber: orderData.number,
          };
          createPaymentMutation(paymentPayload, {
            onSuccess(data) {
              if (data.formUrl) {
                Browser.open({ url: data.formUrl });
              }
              finishOrder(orderData);
            },
          });
        } else {
          finishOrder(orderData);
        }
      },
      onError(error) {
        setShowLoading(false);
        if (error && error.response.data.message) {
          const message: string = t(error.response.data.message as keyof typeof t) ?? t("unexpectedErrorCreateOrder");
          showToast({
            message: message,
            duration: 2000,
            color: "danger",
          });
        }
      },
    });
  };

  const productsMap = useMemo(() => {
    if (!products?.length) return {};

    return products.reduce(
      (mapObj, product) => {
        mapObj[product.id] = product;
        return mapObj;
      },
      {} as Record<string, Product>,
    );
  }, [products]);

  const isContinueButtonDisabled = showLoading;

  return (
    <Page>
      <Page.HeaderMobile showBackButton />
      <Page.Content scrollEvents={true}>
        <Page.HeaderDesktop />
        <IonLoading isOpen={showLoading} onDidDismiss={() => setShowLoading(false)} message={t("Please wait...")} />
        <MaxWidthContainer
          classList={`${cx("order")} global-content`}
          smContainer={true}
          mdContainer={true}
          lgContainer={true}
        >
          <IonRow className={cx("orderWrapper")}>
            <IonCol size={"12"} sizeLg={"6"}>
              <div className={cx("ion-hide-lg-up")}>
                {address && (
                  <OrderSummaryDetails
                    paymentMethodTitle={order.paymentMethod?.title ?? ""}
                    title={t("Order Detail")}
                    address={address}
                    shippingIsDefine={!!address}
                    shippingMethod={order.shippingMethod}
                    deliveryDate={order.deliveryDate}
                    deliveryInterval={order.deliveryInterval}
                  />
                )}
              </div>

              {/* TODO-FIX: THE LIST-PRODUCTS PROP 2-3 AT FIRST RENDERS UNDEFINED AND THEREFORE PRODUCT IMAGES ARE NOT DISPLAYED */}

              <OrderSummaryProductsList
                title={t("Products")}
                productsMap={productsMap}
                loading={isProductsLoading}
                cartProducts={cartProducts}
              />

              {(!!coupons.length || rewardPoints > 0) && (
                <OrderSummaryCoupons
                  setLoading={() => {
                    setShowLoading(false);
                  }}
                  coupons={coupons}
                  rewardPoints={rewardPoints}
                />
              )}

              <OrderSummaryTotal
                title={t("Sub Total")}
                productTotal={cartSubtotalValue}
                deliveryAmount={order.shippingMethod?.cost || 0}
                discount={cartSubtotalValue - cartTotalValue}
              />

              <div className="ion-hide-lg-up">
                {order.customerNote && <OrderSummaryNote title={t("Order Notes")} note={order.customerNote} />}
              </div>

              <div className="ion-hide-lg-up">
                {order.whatsAppNumber && (
                  <OrderSummaryWhatsapp title={t("WhatsApp number")} whatsAppNumber={order.whatsAppNumber} />
                )}
              </div>

              <IonCard className={cx("orderToolbarWrapper", "ion-hide-md-down", "ion-hide-lg-up")}>
                <ContinueButton onClick={submit} disabled={isContinueButtonDisabled} buttonText={t("SendOrder")} />
              </IonCard>
            </IonCol>
            <IonCol size={"12"} sizeLg={"6"} className={cx("ion-hide-lg-down")}>
              {address && (
                <OrderSummaryDetails
                  paymentMethodTitle={order.paymentMethod?.title ?? ""}
                  title={t("Order Detail")}
                  address={address}
                  shippingIsDefine={!!address}
                  shippingMethod={order.shippingMethod}
                  deliveryDate={order.deliveryDate}
                  deliveryInterval={order.deliveryInterval}
                />
              )}
              {order.customerNote && <OrderSummaryNote title={t("Order Notes")} note={order.customerNote} />}
              {order.whatsAppNumber && (
                <OrderSummaryWhatsapp title={t("WhatsApp number")} whatsAppNumber={order.whatsAppNumber} />
              )}
              <IonCard className={cx("orderToolbarWrapper")}>
                <ContinueButton
                  onClick={() => submit()}
                  disabled={isContinueButtonDisabled}
                  buttonText={t("SendOrder")}
                />
              </IonCard>
            </IonCol>
          </IonRow>
        </MaxWidthContainer>
        <Page.ContentFooter />
      </Page.Content>
      <Page.PageFooter className={cx("oneButtonFooter")}>
        <div className={cx("ion-hide-md-up")}>
          <ContinueButton onClick={submit} disabled={isContinueButtonDisabled} buttonText={t("SendOrder")} />
        </div>
      </Page.PageFooter>
    </Page>
  );
};

export default Order;
